'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  createCaseUploadLink,
  deleteCaseDocument,
  fetchCaseDocumentDownload,
  fetchCaseDocumentPassword,
  revokeCaseUploadLink,
} from '@/features/cases/api/cases.api'
import type {
  CaseDocumentDownload,
  CaseDocumentPasswordResult,
  CaseDocumentKind,
  CaseDocumentSummary,
  CaseUploadLinkCreated,
} from '@/features/cases/types'
import { useFileUpload } from '@/lib/upload/useFileUpload'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

/** The first real caller of `useFileUpload` (built in Phase 0, XHR-based for progress events
 * `fetch` can't provide). `progress`/`isUploading`/`cancel` are merged onto the mutation's own
 * return value so `UploadDocumentDialog` only deals with one object. */
export function useUploadCaseDocument(caseId: string) {
  const { upload, progress, isUploading, cancel } = useFileUpload()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      file,
      kind,
      password,
    }: {
      file: File
      kind: CaseDocumentKind
      password?: string
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('kind', kind)
      if (password) formData.append('password', password)
      return upload<CaseDocumentSummary>(`/api/v1/cases/${caseId}/documents`, formData)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.documents(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Document uploaded' })
    },
    onError: toastError,
  })
  return { ...mutation, progress, isUploading, cancel }
}

export function useDeleteCaseDocument(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => deleteCaseDocument(authedFetch, caseId, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.documents(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Document deleted' })
    },
    onError: toastError,
  })
}

/** A fetch-on-demand action, not a cached read — a download URL carries a 10-minute TTL, so it
 * doesn't belong in the query cache. No toast: `CaseDocuments` normalizes the URL and opens it. */
export function useCaseDocumentDownload(caseId: string) {
  const authedFetch = useApiFetch()
  return useMutation<CaseDocumentDownload, unknown, string>({
    mutationFn: (documentId: string) => fetchCaseDocumentDownload(authedFetch, caseId, documentId),
  })
}

/** Fetch-on-demand, same reasoning as the download URL — a password is meant to be seen once, not
 * cached. `CaseDocuments` doesn't catch its own reveal errors, so this hook surfaces them itself. */
export function useCaseDocumentPassword(caseId: string) {
  const authedFetch = useApiFetch()
  return useMutation<CaseDocumentPasswordResult, unknown, string>({
    mutationFn: (documentId: string) => fetchCaseDocumentPassword(authedFetch, caseId, documentId),
    onError: toastError,
  })
}

/** No toast — `CaseUploadLinks` owns the reveal-once dialog, the same "capture at call site"
 * reasoning as Phase 5/6's other reveal-a-secret-once actions. */
export function useCreateCaseUploadLink(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation<CaseUploadLinkCreated, unknown, void>({
    mutationFn: () => createCaseUploadLink(authedFetch, caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.uploadLinks(caseId) })
    },
    onError: toastError,
  })
}

export function useRevokeCaseUploadLink(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (linkId: string) => revokeCaseUploadLink(authedFetch, caseId, linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.uploadLinks(caseId) })
      toast({ variant: 'success', title: 'Link revoked' })
    },
    onError: toastError,
  })
}
